/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/vue'
import ReadinessCheckerApi from '@/api/readinessChecker'
import kvSyncSubscriber from '@/mqtt/kvSync'
// import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'

const SET_POTENTIAL_ISSUES = 'SET_POTENTIAL_ISSUES'
const ADD_POTENTIAL_ISSUES = 'ADD_POTENTIAL_ISSUES'
const SET_IS_TRIGGERING = 'SET_IS_TRIGGERING'
const SET_TRIGGER_FAILED = 'SET_TRIGGER_FAILED'
const REMOVE_ISSUE = 'REMOVE_ISSUE'
const SET_LAST_CHECKED = 'SET_LAST_CHECKED'
const COUNT_POTENTIAL_ISSUES = 'COUNT_POTENTIAL_ISSUES'
const SET_CURRENT_RUN_ID = 'SET_CURRENT_RUN_ID'
const REMOVE_RESOLVED_ISSUES = 'REMOVE_RESOLVED_ISSUES'
const SET_HAS_AUTO_TRIGGERED = 'SET_HAS_AUTO_TRIGGERED'

const _state = {
  potentialIssues: [],
  isTriggering: false,
  triggerFailed: false,
  lastChecked: null,
  currentRunId: null,
  hasAutoTriggered: false, // Track if auto-trigger has happened
}

const _getters = {
  potentialIssues(state) {
    return state.potentialIssues
  },

  isTriggering(state) {
    return state.isTriggering
  },

  triggerFailed(state) {
    return state.triggerFailed
  },

  lastChecked(state) {
    return state.lastChecked
  },

  currentRunId(state) {
    return state.currentRunId
  },

  canUseReadinessChecker: (_, __, ___, rootGetters) => {
    return rootGetters['featureToggles/isFeatureEnabled']('release-AHA-40468-readiness-checker')
  },

  canRecheck: (_, __, rootState) => {
    // Re-check is only allowed when presentation has 5+ slides
    const slideCount = rootState.presentation?.slides?.length || 0
    return slideCount >= 5
  },

  issuesByType: (state) => (type) => {
    return state.potentialIssues.filter((issue) => issue.type === type)
  },

  issuesByScope: (state) => (scope) => {
    return state.potentialIssues.filter((issue) => issue.scope === scope)
  },

  issuesForSlide: (state) => (slideId) => {
    return state.potentialIssues.filter(
      (issue) => issue.scope === 'slide' && issue.slideId === slideId,
    )
  },

  issueCount(state) {
    return state.potentialIssues.length
  },

  needsAttentionIssues(state) {
    // Critical issues: severity = 3
    return state.potentialIssues.filter((issue) => issue.severity === 3)
  },

  moreSuggestionsIssues(state) {
    // Optional improvements: severity != 3
    return state.potentialIssues.filter((issue) => issue.severity !== 3)
  },
}

const mutations = {
  [SET_POTENTIAL_ISSUES](state, issues) {
    state.potentialIssues = issues
  },
  [COUNT_POTENTIAL_ISSUES](state, issues) {
    state.potentialIssues = issues.length
  },

  [ADD_POTENTIAL_ISSUES](state, issues) {
    if (
      state.potentialIssues.length > 0 &&
      issues.some(
        (issue) => issue.createdAt > state.potentialIssues[0].createdAt,
      )
    ) {
      state.potentialIssues = issues
      state.lastChecked = issues[0].createdAt
    } else {
      state.potentialIssues = [...state.potentialIssues, ...issues]
      if (!state.lastChecked) {
        state.lastChecked = issues[0].createdAt
      }
    }
  },

  [SET_IS_TRIGGERING](state, { isTriggering }) {
    state.isTriggering = isTriggering
  },

  [SET_TRIGGER_FAILED](state, { triggerFailed }) {
    state.triggerFailed = triggerFailed
  },

  [REMOVE_ISSUE](state, { issueId }) {
    const issueIndex = state.potentialIssues.findIndex((issue, index) => index === issueId)
    if (issueIndex >= 0) {
      state.potentialIssues.splice(issueIndex, 1)
    }
  },

  [SET_LAST_CHECKED](state, lastChecked) {
    state.lastChecked = lastChecked
  },

  [SET_CURRENT_RUN_ID](state, runId) {
    state.currentRunId = runId
  },

  [REMOVE_RESOLVED_ISSUES](state, resolvedIssueIndices) {
    // Remove issues by their array indices
    // Sort in descending order to remove from end to start (prevents index shifting)
    const sortedIndices = [...resolvedIssueIndices].sort((a, b) => b - a)
    sortedIndices.forEach((index) => {
      if (index >= 0 && index < state.potentialIssues.length) {
        state.potentialIssues.splice(index, 1)
      }
    })
  },

  [SET_HAS_AUTO_TRIGGERED](state, hasAutoTriggered) {
    state.hasAutoTriggered = hasAutoTriggered
  },
}

const getReadinessCheckerTopic = (presentationId) => {
  return `presentation-${presentationId}-readiness-checker`
}

const actions = {
  subscribeReadinessChecker({
    dispatch, commit, state,
  }, { presentationId }) {
    if (!presentationId) return
    const topic = getReadinessCheckerTopic(presentationId)
    kvSyncSubscriber.subscribeKey(topic, (_, message) => {
      try {
        const payload = JSON.parse(message)

        // Handle new issues
        if (payload.issues) {
          dispatch('handleReadinessCheckerResultAction', {
            potentialIssues: payload.issues || [],
          })
        }

        // Handle resolved issues
        if (payload.resolvedIssues && Array.isArray(payload.resolvedIssues)) {
          commit(REMOVE_RESOLVED_ISSUES, payload.resolvedIssues)
        }
        // Handle finished run
        if (payload.finishedRun) {
          // Only stop loading if this is the current run
          if (payload.finishedRun === state.currentRunId) {
            commit(SET_IS_TRIGGERING, { isTriggering: false })
            commit(SET_CURRENT_RUN_ID, null)
          }
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'readiness-checker-mqtt' },
        })
      }
    })
  },

  unsubscribeReadinessChecker(_, { presentationId }) {
    if (!presentationId) return
    kvSyncSubscriber.unsubscribeKey(getReadinessCheckerTopic(presentationId))
  },

  async triggerReadinessCheckerAction({ commit, rootState, getters }) {
    try {
      commit(SET_TRIGGER_FAILED, { triggerFailed: false })

      const presentationId = rootState.presentation?.id

      if (!presentationId || !getters.canRecheck) {
        return
      }

      commit(SET_IS_TRIGGERING, { isTriggering: true })

      const response = await ReadinessCheckerApi.triggerReadinessChecker({ presentationId })
      if (response?.runId) {
        commit(SET_CURRENT_RUN_ID, response.runId)
      }
    } catch (error) {
      commit(SET_IS_TRIGGERING, { isTriggering: false })
      commit(SET_TRIGGER_FAILED, { triggerFailed: true })

      Sentry.captureException(error, {
        tags: { feature: 'readiness-checker-trigger' },
        extra: { error },
      })
    }
  },

  async getReadinessPotentialIssuesAction({ commit, rootState, getters }) {
    const presentationId = rootState.route.params.id

    if (!presentationId || !getters.canRecheck) {
      return
    }

    const potentialIssues = await ReadinessCheckerApi.getReadinessPotentialIssues({ presentationId })

    if (potentialIssues && potentialIssues.length > 0) {
      commit(ADD_POTENTIAL_ISSUES, potentialIssues)
    }
    commit(SET_IS_TRIGGERING, { isTriggering: false })
  },

  handleReadinessCheckerResultAction({ commit }, { potentialIssues }) {
    if (potentialIssues && potentialIssues.length > 0) {
      commit(ADD_POTENTIAL_ISSUES, potentialIssues)
    } else {
      commit(SET_IS_TRIGGERING, { isTriggering: false })
    }
  },

  resetReadinessCheckerAction({ commit }) {
    commit(SET_POTENTIAL_ISSUES, [])
    commit(SET_IS_TRIGGERING, { isTriggering: false })
    commit(SET_TRIGGER_FAILED, { triggerFailed: false })
    commit(SET_LAST_CHECKED, null)
    commit(SET_CURRENT_RUN_ID, null)
    commit(SET_HAS_AUTO_TRIGGERED, false)
  },

  async tryAutoTriggerReadinessCheckerAction({
    state, commit, dispatch, getters,
  }) {
    // Guard 1: Already auto-triggered
    if (state.hasAutoTriggered) return

    // Guard 2: Not enough slides
    if (!getters.canRecheck) return

    // Guard 3: Already have issues or currently checking
    if (!state.potentialIssues.length && !state.isTriggering) {
      // Step 1: Try to fetch existing issues from backend
      await dispatch('getReadinessPotentialIssuesAction')

      // Step 2: Only trigger new check if no issues found
      if (!state.potentialIssues.length) {
        await dispatch('triggerReadinessCheckerAction')
      }

      // Mark as auto-triggered to prevent duplicate triggers
      commit(SET_HAS_AUTO_TRIGGERED, true)
    }
  },

  dismissIssueAction({ commit }, { issueId }) {
    commit(REMOVE_ISSUE, { issueId })
  },

  async recheckReadinessAction({ commit, dispatch }) {
    // Step 1: Set loading and clear all issues
    commit(SET_IS_TRIGGERING, { isTriggering: true })
    commit(SET_TRIGGER_FAILED, { triggerFailed: false })
    commit(SET_LAST_CHECKED, null)
    commit(SET_CURRENT_RUN_ID, null)

    // Step 2: Trigger readiness checker
    await dispatch('triggerReadinessCheckerAction')
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
